import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api } from "../lib/api";
import type { FoodReport, ReportStatus } from "../lib/types";
import { fmtRelative } from "../lib/format";

const STATUS_VARIANT: Record<
  ReportStatus,
  "default" | "neutral" | "success" | "error" | "warning"
> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  superseded: "neutral",
};

const STATUSES: { id: ReportStatus; label: string }[] = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "superseded", label: "Superseded" },
];

export function ReportsPage() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
          Reports
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          Triage user-submitted corrections. Approve to apply, reject to
          discard.
        </p>
      </header>

      <PendingSection />

      <section>
        <h2 className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-50">
          History
        </h2>
        {STATUSES.filter((s) => s.id !== "pending").map((s) => (
          <CollapsibleStatus key={s.id} status={s.id} label={s.label} />
        ))}
      </section>
    </div>
  );
}

function PendingSection() {
  const { data, isLoading, error } = useQuery<FoodReport[]>({
    queryKey: ["reports", "pending"],
    queryFn: () =>
      api<FoodReport[]>("/v1/admin/reports?status=pending&limit=200"),
  });

  if (isLoading) return <Skeleton />;
  if (error)
    return (
      <Callout title="Failed to load" variant="error">
        {(error as Error).message}
      </Callout>
    );

  if (!data || data.length === 0) {
    return (
      <Card>
        <p className="text-center text-sm text-gray-500 dark:text-gray-500">
          Inbox zero. Nothing pending review.
        </p>
      </Card>
    );
  }

  return (
    <section>
      <p className="mb-3 text-sm text-gray-500 dark:text-gray-500">
        {data.length} awaiting review.
      </p>
      <ReportTable rows={data} withActions />
    </section>
  );
}

function CollapsibleStatus({
  status,
  label,
}: {
  status: ReportStatus;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery<FoodReport[]>({
    queryKey: ["reports", status],
    queryFn: () =>
      api<FoodReport[]>(
        `/v1/admin/reports?status=${encodeURIComponent(status)}&limit=50`,
      ),
    enabled: open,
  });

  return (
    <div className="border-t border-gray-200 dark:border-gray-900">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 py-3 text-left text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-50"
      >
        <ChevronRight
          size={14}
          className={`transition-transform ${open ? "rotate-90" : ""}`}
        />
        Show {label.toLowerCase()}
      </button>
      {open && (
        <div className="space-y-3 pb-4">
          {isLoading && <Skeleton />}
          {error && (
            <Callout title="Failed to load" variant="error">
              {(error as Error).message}
            </Callout>
          )}
          {data && <ReportTable rows={data} withActions={false} />}
        </div>
      )}
    </div>
  );
}

function ReportTable({
  rows,
  withActions,
}: {
  rows: FoodReport[];
  withActions: boolean;
}) {
  if (rows.length === 0) {
    return (
      <Card>
        <p className="text-center text-sm text-gray-500 dark:text-gray-500">
          No reports.
        </p>
      </Card>
    );
  }

  return (
    <TableRoot>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Created</TableHeaderCell>
            <TableHeaderCell>Food</TableHeaderCell>
            <TableHeaderCell>Kind</TableHeaderCell>
            <TableHeaderCell>Reporter</TableHeaderCell>
            <TableHeaderCell>Note</TableHeaderCell>
            <TableHeaderCell>Proposed</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            {withActions && <TableHeaderCell>Actions</TableHeaderCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <ReportRow key={r.id} row={r} withActions={withActions} />
          ))}
        </TableBody>
      </Table>
    </TableRoot>
  );
}

function ReportRow({
  row,
  withActions,
}: {
  row: FoodReport;
  withActions: boolean;
}) {
  const qc = useQueryClient();
  const resolve = useMutation({
    mutationFn: async (decision: "approved" | "rejected") =>
      api<FoodReport>(`/v1/admin/reports/${row.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ decision, reviewed_by: "admin" }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reports"] }),
  });

  const proposed = (() => {
    if (!row.proposed) return null;
    try {
      return JSON.parse(row.proposed) as Record<string, unknown>;
    } catch {
      return null;
    }
  })();

  return (
    <TableRow className="align-top">
      <TableCell className="whitespace-nowrap">
        {fmtRelative(row.created_at)}
      </TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs">
        {row.food_id.slice(0, 8)}
      </TableCell>
      <TableCell className="whitespace-nowrap">{row.kind}</TableCell>
      <TableCell className="whitespace-nowrap">
        {row.reporter_hint ?? "—"}
      </TableCell>
      <TableCell className="max-w-[260px] whitespace-normal">
        {row.note ?? "—"}
      </TableCell>
      {/* Proposed cell: explicit width + whitespace-pre so the JSON can wrap
          inside its own <pre> instead of escaping the cell and overlapping
          neighbouring columns. Tremor's <TableRoot> applies whitespace-nowrap
          at the wrapper level, so we have to override it here. */}
      <TableCell className="w-[300px] min-w-[300px] whitespace-normal">
        {proposed ? (
          <pre className="overflow-x-auto whitespace-pre rounded-md bg-gray-50 p-2 font-mono text-xs leading-snug text-gray-700 dark:bg-gray-900 dark:text-gray-400">
            {JSON.stringify(proposed, null, 2)}
          </pre>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
      </TableCell>
      {withActions && (
        <TableCell>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              isLoading={resolve.isPending}
              onClick={() => {
                if (!confirm("Approve and apply this report?")) return;
                resolve.mutate("approved");
              }}
              className="!px-2.5 !py-1 !text-xs"
            >
              Approve
            </Button>
            <Button
              variant="destructive"
              isLoading={resolve.isPending}
              onClick={() => {
                if (!confirm("Reject this report?")) return;
                resolve.mutate("rejected");
              }}
              className="!px-2.5 !py-1 !text-xs"
            >
              Reject
            </Button>
          </div>
          {resolve.error && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-500">
              {(resolve.error as Error).message}
            </p>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="h-10 w-full animate-pulse rounded-md bg-gray-100 dark:bg-gray-900"
        />
      ))}
    </div>
  );
}
